import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type {
  AiCredential,
  AiImplementationRun,
  AiImplementationAvailability,
  SaveAiCredentialRequest,
  GitHubDeviceFlowInitiateResponse,
  GitHubDeviceFlowPollRequest,
} from '../types';
import { toCamelCase } from '../../grid/utils';

export const AI_IMPLEMENTATION_API_PATH = '/umbraco/api/AiImplementation';

export const aiImplementationApi = createApi({
  reducerPath: 'aiImplementationApi',
  baseQuery,
  tagTypes: ['AiCredentials'],
  endpoints: (builder) => ({
    getAiCredentials: builder.query<AiCredential[], void>({
      query: () => `${AI_IMPLEMENTATION_API_PATH}/Credentials`,
      transformResponse: (response: unknown) => toCamelCase(response) as AiCredential[],
      providesTags: ['AiCredentials'],
    }),
    saveAiCredential: builder.mutation<AiCredential, SaveAiCredentialRequest>({
      query: (body) => ({
        url: `${AI_IMPLEMENTATION_API_PATH}/SaveCredential`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => toCamelCase(response) as AiCredential,
      invalidatesTags: ['AiCredentials'],
    }),
    deleteAiCredential: builder.mutation<void, number>({
      query: (id) => ({
        url: `${AI_IMPLEMENTATION_API_PATH}/DeleteCredential?id=${encodeURIComponent(id)}`,
        method: 'POST',
      }),
      invalidatesTags: ['AiCredentials'],
    }),
    startAiImplementation: builder.mutation<
      AiImplementationRun,
      { taskId: number; credentialId: number }
    >({
      query: (body) => ({
        url: `${AI_IMPLEMENTATION_API_PATH}/Start`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => toCamelCase(response) as AiImplementationRun,
    }),
    getAiImplementationAvailability: builder.query<AiImplementationAvailability, number>({
      query: (taskId) => `${AI_IMPLEMENTATION_API_PATH}/Availability?taskId=${taskId}`,
      transformResponse: (response: unknown) =>
        toCamelCase(response) as AiImplementationAvailability,
    }),
    getAiImplementationRun: builder.query<AiImplementationRun, string>({
      query: (runId) => `${AI_IMPLEMENTATION_API_PATH}/Run?runId=${encodeURIComponent(runId)}`,
      transformResponse: (response: unknown) => toCamelCase(response) as AiImplementationRun,
    }),
    getActiveAiImplementationRun: builder.query<AiImplementationRun | null, number>({
      query: (taskId) => `${AI_IMPLEMENTATION_API_PATH}/Active?taskId=${taskId}`,
      transformResponse: (response: unknown) =>
        response ? (toCamelCase(response) as AiImplementationRun) : null,
    }),
    cancelAiImplementationRun: builder.mutation<void, string>({
      query: (runId) => ({
        url: `${AI_IMPLEMENTATION_API_PATH}/Cancel?runId=${encodeURIComponent(runId)}`,
        method: 'POST',
      }),
    }),
    initiateGitHubDeviceAuth: builder.mutation<GitHubDeviceFlowInitiateResponse, void>({
      query: () => ({
        url: `${AI_IMPLEMENTATION_API_PATH}/InitiateGitHubDeviceAuth`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        toCamelCase(response) as GitHubDeviceFlowInitiateResponse,
    }),
    pollGitHubDeviceAuth: builder.mutation<
      { success: boolean; status?: string },
      GitHubDeviceFlowPollRequest
    >({
      query: (body) => ({
        url: `${AI_IMPLEMENTATION_API_PATH}/PollGitHubDeviceAuth`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        toCamelCase(response) as { success: boolean; status?: string },
    }),
  }),
});

export const {
  useGetAiCredentialsQuery,
  useSaveAiCredentialMutation,
  useDeleteAiCredentialMutation,
  useStartAiImplementationMutation,
  useGetAiImplementationAvailabilityQuery,
  useGetAiImplementationRunQuery,
  useGetActiveAiImplementationRunQuery,
  useCancelAiImplementationRunMutation,
  useInitiateGitHubDeviceAuthMutation,
  usePollGitHubDeviceAuthMutation,
} = aiImplementationApi;
